import { useEffect } from 'react';
import { FastingProtocol } from '@/types/fasting';
import { useFastingHistory, FastingSession } from '../FastingHistoryContext';
import { useToast } from '@/hooks/use-toast';
import { useMilestoneChecker } from './useMilestoneChecker';

interface TimerIntervalProps {
  isActive: boolean;
  isPaused: boolean;
  startTime: number | null;
  pausedTime: number | null;
  totalPauseDuration: number;
  timeElapsed: number;
  selectedProtocol: FastingProtocol;
  intervalRef: React.MutableRefObject<NodeJS.Timeout | null>;
  setTimeElapsed: (value: number) => void;
  setPausedTime: (value: number | null) => void;
  setTotalPauseDuration: (value: number | ((prev: number) => number)) => void;
  setIsActive: (value: boolean) => void;
}

export const useTimerInterval = ({
  isActive,
  isPaused,
  startTime,
  pausedTime,
  totalPauseDuration,
  timeElapsed,
  selectedProtocol,
  intervalRef,
  setTimeElapsed,
  setPausedTime,
  setTotalPauseDuration,
  setIsActive
}: TimerIntervalProps) => {
  const fastingHistory = useFastingHistory();
  const { toast } = useToast();
  const { checkMilestones } = useMilestoneChecker(selectedProtocol);

  // Core timer logic - this is responsible for continuously updating timeElapsed
  useEffect(() => {
    // Clear any existing interval when this effect runs
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Start interval only when timer is active and not paused
    if (isActive && !isPaused) {
      // Handle pause resumption
      if (pausedTime) {
        const pauseDuration = Date.now() - pausedTime;
        setTotalPauseDuration(prev => prev + pauseDuration);
        setPausedTime(null);
      }

      // Ensure we have a valid start time
      if (!startTime) {
        console.error("[useTimerLogic] Timer active but startTime is null!");
        return;
      }

      // Force immediate calculation to sync with current time
      const now = Date.now();
      const currentElapsed = Math.floor(((now - startTime) - totalPauseDuration) / 1000);
      const boundedElapsed = Math.max(0, Math.min(currentElapsed, selectedProtocol.durationHours * 60 * 60));
      
      // Update immediately if there's a significant difference
      if (Math.abs(timeElapsed - boundedElapsed) > 1) {
        console.log("[useTimerInterval] Syncing elapsed time:", { from: timeElapsed, to: boundedElapsed });
        setTimeElapsed(boundedElapsed);
      }
      
      // Store the previous elapsed time to avoid stale closures
      let lastElapsed = boundedElapsed;
      
      // Set up the interval for ongoing updates
      intervalRef.current = setInterval(() => {
        if (startTime) {
          const now = Date.now();
          const currentElapsed = Math.floor(((now - startTime) - totalPauseDuration) / 1000);
          
          // Check for milestones before updating
          checkMilestones(lastElapsed, currentElapsed);
          lastElapsed = currentElapsed;
          
          // Update the elapsed time
          setTimeElapsed(Math.max(0, currentElapsed));
          
          // Check if fast is complete
          if (currentElapsed >= selectedProtocol.durationHours * 60 * 60) {
            if (fastingHistory && startTime) {
              const session = {
                id: `fast-${now}`,
                startTime,
                endTime: now,
                duration: selectedProtocol.durationHours * 60 * 60,
                targetDuration: selectedProtocol.durationHours * 60 * 60,
                completed: true,
                protocolId: selectedProtocol.id,
                protocolName: selectedProtocol.name,
                date: new Date().toISOString().split('T')[0]
              };
              fastingHistory.addSession(session);
            }
            
            clearInterval(intervalRef.current!);
            intervalRef.current = null;
            setIsActive(false);
            
            toast({
              title: "Fast Complete!",
              description: `Congratulations! You've completed your ${selectedProtocol.name} fast.`,
              variant: "default",
            });
          }
        }
      }, 1000);
    } else if (isActive && isPaused && !pausedTime) {
      // Record pause start time if just paused
      setPausedTime(Date.now());
    }
    
    // Clean up interval on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, isPaused, startTime, pausedTime, totalPauseDuration, selectedProtocol, fastingHistory, toast, checkMilestones, intervalRef, setTimeElapsed, setPausedTime, setTotalPauseDuration, setIsActive]);
};
